// Add User modal. Since the username rollout (2026-10-19) a new account is
// username-only: no colour, a login handle (checked live against the BE),
// and a policy password the admin hands over — temporary by default, the
// user rotates it at first login. Managers/Admins must have an email.

import { POSITIONS, positionLabelKey, isAdminPosition, isManagerPlus, readUserPosition } from "../access/positions";
import { X, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import "../styles/add-user.css";
import { FullUserDetails } from "../types/User";
import { useTranslation } from "../i18n";
import { useReadOnly } from "../access/ReadOnly";
import PasswordFields from "./PasswordFields";
import { useUsernameAvailability } from "../hooks/useUsernameAvailability";
import { isPolicyCompliant, passwordPolicyFor } from "./helpers/passwordPolicy";
import { normaliseUsername, usernameErrorKey, USERNAME_MAX } from "./helpers/usernameRules";
import { isValidEmail } from "./helpers/emailFormat";

const UserAdd = ({ addUser, closeFunction }: { addUser: (user: FullUserDetails) => void; closeFunction: () => void; }) => {
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    const [userWarehouse, setUserWarehouse] = useState("Pembroke");
    const [userName, setUserName] = useState("");
    const [username, setUsername] = useState("");
    const [userRFID, setUserRFID] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userPassword, setUserPassword] = useState("");
    const [userPermissions, setUserPermissions] = useState("Operative");
    const selfIsAdmin = isAdminPosition(readUserPosition());
    const [loading, set_Loading] = useState(false);

    const managerPlus = isManagerPlus(userPermissions);
    const policy = passwordPolicyFor(userPermissions);
    const passwordContext = { username, name: userName };
    const availability = useUsernameAvailability(username, { enabled: true });
    const emailTrimmed = userEmail.trim();
    const emailOk = managerPlus ? isValidEmail(emailTrimmed) : (emailTrimmed === "" || isValidEmail(emailTrimmed));
    const usernameOk = availability.status === "available" || availability.status === "unknown";
    const canSubmit = !readOnly && !loading
        && userName.trim() !== ""
        && usernameOk
        && emailOk
        && isPolicyCompliant(userPassword, policy, passwordContext);

    // Submit button
    const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmit) return;

        // Set loading state
        set_Loading(true);

        // Get form data
        const data: FullUserDetails = {
            UID: 0,
            warehouse: userWarehouse,
            name: userName.trim(),
            username,
            password: userPassword,
            permissions: userPermissions,
            userRFID: userRFID.trim(),
            email: emailTrimmed === "" ? null : emailTrimmed,
        };

        // Add user
        addUser(data);

        // Reset Loading state
        set_Loading(false);
    };

    const usernameHint = () => {
        const code = availability.status === "invalid" || availability.status === "unavailable" ? availability.code : null;
        if (code) return <span className="user-edit-field-hint user-edit-field-hint--error">{t(usernameErrorKey(code))}</span>;
        if (availability.status === "checking") return <span className="user-edit-field-hint"><Loader2 size={12} aria-hidden="true" /> {t("username_checking")}</span>;
        if (availability.status === "available") return <span className="user-edit-field-hint user-edit-field-hint--ok"><Check size={12} aria-hidden="true" /> {t("username_available")}</span>;
        return <span className="user-edit-field-hint">{t("account_setup_username_hint")}</span>;
    };

    return (
        <div className="modal-overlay" onClick={closeFunction}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("add_user")}</h2>
                    <button className="modal-close" onClick={closeFunction}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                <form className="modal-form" onSubmit={handleSubmit}>
                    <label>
                        <span className="modal-form-label">{t("name")}</span>
                        <input type="text" name="display-name" autoComplete="off" placeholder={t("name")} value={userName} onChange={(e) => setUserName(e.target.value)} />
                    </label>
                    <label>
                        <span className="modal-form-label">{t("users_col_username")}</span>
                        <input
                            type="text"
                            name="login-username"
                            autoComplete="off"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={USERNAME_MAX}
                            placeholder={t("users_col_username")}
                            value={username}
                            onChange={(e) => setUsername(normaliseUsername(e.target.value))}
                        />
                        {usernameHint()}
                    </label>
                    <label>
                        <span className="modal-form-label">{t("warehouse")}</span>
                        <select name="warehouse" required value={userWarehouse} onChange={(e) => setUserWarehouse(e.target.value)}>
                            <option value="Pembroke">{t("pembroke_dock")}</option>
                            <option value="Newport">{t("newport")}</option>
                            <option value="Poland">{t("poland")}</option>
                        </select>
                    </label>
                    <label>
                        <span className="modal-form-label">{t("users_col_permissions")}</span>
                        <select name="permissions" value={userPermissions} onChange={(e) => setUserPermissions(e.target.value)}>
                            {/* Only an Admin may create an Admin (the BE enforces it too). */}
                            {POSITIONS.filter(p => p !== "Admin" || selfIsAdmin).map(p => (
                                <option key={p} value={p}>{t(positionLabelKey(p))}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        <span className="modal-form-label">{managerPlus ? t("email_required") : t("email_optional")}</span>
                        <input type="email" name="email" autoComplete="off" placeholder={t("email_placeholder")} value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
                        {managerPlus && !isValidEmail(emailTrimmed) && (
                            <span className="user-edit-field-hint user-edit-field-hint--error">{t("users_email_required_manager")}</span>
                        )}
                    </label>
                    <label>
                        <span className="modal-form-label">{t("rfid_optional")}</span>
                        <input type="text" name="rfid" autoComplete="off" placeholder={t("rfid_optional")} value={userRFID} onChange={(e) => setUserRFID(e.target.value)} />
                    </label>
                    <div>
                        <span className="modal-form-label">{t("password")}</span>
                        <p className="user-edit-check-hint">{t("users_add_password_hint")}</p>
                        <PasswordFields
                            variant="light"
                            policy={policy}
                            context={passwordContext}
                            newPassword={userPassword}
                            onNewPasswordChange={setUserPassword}
                            requireConfirm={false}
                            showGenerator
                            defaultRevealed
                            autoCompleteMode="off"
                        />
                    </div>
                    <button
                        type="submit"
                        className="btn btn-primary btn-full"
                        disabled={!canSubmit}
                        aria-disabled={!canSubmit}
                        title={readOnly ? t("read_only_mode") : undefined}
                    >
                        {loading ? t("adding_user") : t("add_user")}
                    </button>
                </form>
                </div>
            </div>
        </div>
    );
};

export default UserAdd;
